import { UserRole, type AccessTokenType } from "@/types";

export type RbacRule = {
  match: string | RegExp | ((path: string) => boolean);
  denyRoles?: UserRole[];
};

export function matches(rule: RbacRule, path: string): boolean {
  if (typeof rule.match === "string") return rule.match === path;
  if (rule.match instanceof RegExp) return rule.match.test(path);
  return rule.match(path);
}

export function intersects(a: readonly UserRole[], b: readonly UserRole[]) {
  if (a.length === 0 || b.length === 0) return false;
  const setB = new Set(b);
  return a.some((x) => setB.has(x));
}

export const RBAC_RULES: RbacRule[] = [
  { match: "/templates", denyRoles: [UserRole.Viewer, UserRole.SegViewer] },

  {
    match: /^\/notifications\/(channels|rules)$/,
    denyRoles: [UserRole.Viewer, UserRole.SegViewer],
  },

  {
    match: "/settings-auth",
    denyRoles: [UserRole.Viewer, UserRole.SegAdmin, UserRole.SegViewer],
  },

  {
    match: "/general-settings/license",
    denyRoles: [
      UserRole.Viewer,
      UserRole.Guest,
      UserRole.SegAdmin,
      UserRole.SegViewer,
    ],
  },

  {
    match: "/events/netflow",
    denyRoles: [UserRole.SegAdmin, UserRole.SegViewer],
  },
];

export function isAdminOrSegAdmin(roles: readonly UserRole[]): boolean {
  return roles.includes(UserRole.Admin) || roles.includes(UserRole.SegAdmin);
}

export function canCreateEntities(roles: readonly UserRole[]): boolean {
  return !intersects(roles, [UserRole.Viewer, UserRole.SegViewer]);
}

export function canCreateExternals(roles: readonly UserRole[]): boolean {
  return roles.includes(UserRole.Admin);
}

export function isAllowedByRbac(options: {
  path: string;
  token: AccessTokenType | null;
}): boolean {
  const rule = RBAC_RULES.find((r) => matches(r, options.path));
  if (!rule) return true;

  const roles = options.token?.roles ?? [];

  if (rule.denyRoles?.length && intersects(roles, rule.denyRoles)) return false;
  return true;
}
